import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useEntitlements } from '@shared/license';
import { STRIPE_STARTER, STRIPE_PRO, STRIPE_PRO_ANNUAL } from '@shared/config/stripe';
import { track } from '@shared/events';
import { resolveLicenseGateFeatureKey } from './LicenseGate.utils';

type LicenseGatePreviewItem = {
  label: string;
  value: string;
  note?: string;
};

type LicenseGatePreviewConfig = {
  eyebrow: string;
  title: string;
  intro: string;
  items: LicenseGatePreviewItem[];
  sampleLabel?: string;
  demo?: ReactNode;
};

export function LicenseGate({
  children,
  toolName,
  description,
  preview,
  requiredTier = 'starter',
  featureKey,
}: {
  children: ReactNode;
  toolName: string;
  description: string;
  preview?: LicenseGatePreviewConfig;
  requiredTier?: 'starter' | 'pro';
  featureKey?: string;
}) {
  const { isFeatureEnabled } = useEntitlements();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const previewMode = params.get('preview') === '1';

  const toolSlug = toolName.toLowerCase().replace(/\s+/g, '-');
  const resolvedFeatureKey = resolveLicenseGateFeatureKey(location.pathname, featureKey);
  const previewHref = `${location.pathname}?preview=1`;
  // HashRouter ajoute lui-même le fragment : ne jamais fournir un href qui commence par `#` à <Link>.
  const activationHref = `/tools/activation?tool=${encodeURIComponent(toolSlug)}`;
  const [showComparison, setShowComparison] = useState(false);
  const recommendedTier = requiredTier === 'pro' ? 'Pro' : 'Starter';
  const recommendedHref = requiredTier === 'pro' ? STRIPE_PRO : STRIPE_STARTER;

  const hasAccess = isFeatureEnabled(resolvedFeatureKey);
  const trackUpgradeClick = (tier: string) => {
    track('upgrade_clicked', {
      featureKey: resolvedFeatureKey,
      metadata: { tier, source: 'license_gate', path: location.pathname },
    });
  };
  if (hasAccess) return <>{children}</>;

  if (previewMode && preview) {
    return (
      <div className="page-content">
        <div className="page-hero license-gate-hero" style={{ marginBottom: 24 }}>
          <div>
            <p className="page-badge">{preview.eyebrow}</p>
            <h1 className="page-title" style={{ marginTop: 8 }}>{preview.title}</h1>
            <p className="page-subtitle">{preview.intro}</p>
          </div>
          <div className="license-gate-actions">
            <a
              href={recommendedHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              onClick={() => trackUpgradeClick(requiredTier)}
            >
              Choisir {recommendedTier}
            </a>
            <button type="button" className="btn btn-secondary" onClick={() => setShowComparison((value) => !value)} aria-expanded={showComparison}>
              {showComparison ? 'Masquer les formules' : 'Comparer les formules'}
            </button>
            <Link to="/" className="btn btn-ghost">Retour accueil</Link>
          </div>
        </div>

        <div className="license-gate-recommendation card">
          <div>
            <p className="license-gate-kicker">Formule recommandée</p>
            <h2 className="license-gate-recommendation-title">{recommendedTier}</h2>
            <p className="license-gate-copy">
              {requiredTier === 'pro'
                ? 'Accédez à cet outil et aux fonctions avancées du cockpit.'
                : 'Débloquez l’usage complet de cet outil pour votre activité quotidienne.'}
            </p>
          </div>
        </div>

        {showComparison && (
          <div className="license-gate-comparison" aria-label="Comparaison des formules">
            <div className="card">
              <p className="license-gate-kicker">Starter · 14,90 €/mois</p>
              <strong>Usage quotidien</strong>
              <p className="license-gate-copy">Plus de sessions, suivi et partage.</p>
              <a href={STRIPE_STARTER} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-full" onClick={() => trackUpgradeClick('starter')}>Choisir Starter</a>
            </div>
            <div className="card license-gate-comparison-card--recommended">
              <p className="license-gate-kicker">Pro · 19,90 €/mois</p>
              <strong>Accès avancé</strong>
              <p className="license-gate-copy">Accès complet, export et pilotage avancé.</p>
              <a href={STRIPE_PRO} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-full" onClick={() => trackUpgradeClick('pro')}>Choisir Pro</a>
              <a href={STRIPE_PRO_ANNUAL} target="_blank" rel="noopener noreferrer" className="license-gate-secondary-link" onClick={() => trackUpgradeClick('pro_annual')}>Ou choisir Pro annuel · 199 €/an</a>
            </div>
          </div>
        )}

        {preview.sampleLabel && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ix-text-tertiary)', marginBottom: 8 }}>Aperçu</div>
            <div style={{ fontSize: 13, color: 'var(--ix-text-secondary)' }}>{preview.sampleLabel}</div>
          </div>
        )}

        {preview.demo && (
          <div className="card" style={{ marginBottom: 24 }}>
            {preview.demo}
          </div>
        )}

        <div className="card-stats" style={{ marginBottom: 24 }}>
          {preview.items.map((item) => (
            <div key={item.label} className="card">
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ix-text-tertiary)', marginBottom: 8 }}>{item.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--ix-accent)', marginBottom: 4 }}>{item.value}</div>
              {item.note && <div style={{ fontSize: 12, color: 'var(--ix-text-secondary)' }}>{item.note}</div>}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to={location.pathname} className="btn btn-secondary">← Retour</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div style={{ marginBottom: 24 }}>
        <h1 className="page-heading">{toolName}</h1>
        <p style={{ fontSize: 14, color: 'var(--ix-text-secondary)', marginTop: 6 }}>{description}</p>
      </div>

      <div className="license-gate-grid">

        <div className="card license-gate-recommendation">
          <p className="license-gate-kicker">Formule recommandée · {recommendedTier}</p>
          <div className="license-gate-recommendation-title">Débloquer {toolName}</div>
          <p className="license-gate-copy">
            {requiredTier === 'pro'
              ? 'Cet outil est réservé au forfait Pro.'
              : 'Cet outil est inclus dans le forfait Starter.'}
            {' '}Paiement sécurisé via Stripe. Clé envoyée par email.
          </p>
          <a
            href={recommendedHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-full"
            onClick={() => trackUpgradeClick(requiredTier)}
          >
            Choisir {recommendedTier}
          </a>
          <button type="button" className="btn btn-ghost btn-full license-gate-compare-button" onClick={() => setShowComparison((value) => !value)} aria-expanded={showComparison}>
            {showComparison ? 'Masquer les formules' : 'Comparer les formules'}
          </button>
          {showComparison && (
            <div className="license-gate-inline-comparison">
              <p><strong>Starter</strong> · 14,90 €/mois · usage quotidien.</p>
              <p><strong>Pro</strong> · 19,90 €/mois · fonctions avancées.</p>
              <a href={STRIPE_PRO_ANNUAL} target="_blank" rel="noopener noreferrer" className="license-gate-secondary-link" onClick={() => trackUpgradeClick('pro_annual')}>Pro annuel · 199 €/an</a>
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ix-text-tertiary)', marginBottom: 12 }}>Comment ça marche</div>
          <ol style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              'Clique sur le bouton ci-contre',
              'Paye en ligne — reçois ta clé par email',
              'Entre la clé sur la page d\'activation',
              'L\'outil se débloque dans ce navigateur',
            ].map((step, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: 'var(--ix-text-secondary)' }}>
                <span style={{ minWidth: 20, height: 20, borderRadius: '50%', background: 'var(--ix-accent-subtle)', color: 'var(--ix-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
          <Link to={activationHref} className="btn btn-secondary btn-full" style={{ marginTop: 20 }}>
            J'ai déjà une clé →
          </Link>
        </div>

      </div>

      <div className="license-gate-footer-actions">
        {preview && (
          <Link to={previewHref} className="btn btn-secondary">Voir l'aperçu</Link>
        )}
        <Link to="/" className="btn btn-secondary">Retour accueil</Link>
      </div>
    </div>
  );
}
